import { useState, useEffect } from 'react';

/**
 * Returns up to 2 initials from the name, or null if name is empty.
 */
function getInitials(name) {
  if (!name || !name.trim()) return null;
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * UserAvatar
 *
 * Props:
 * - size: number (diameter in px, default 40)
 * - image: string | undefined — a direct/local image src (equivalent to Flutter's ImageProvider case)
 * - imageUrl: string | undefined — a remote image URL (browser handles caching natively)
 * - name: string | undefined — used to derive initials for the placeholder
 * - onClick: function | undefined
 */
function UserAvatar({ size = 40, image, imageUrl, name, onClick }) {
  const src = image || (imageUrl && imageUrl.length > 0 ? imageUrl : null);

  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  // FIX: reset load/error state whenever the underlying image source
  // changes. Without this, a component instance reused for a different
  // user (e.g. inside a list without a stable per-user `key`) could keep a
  // stale `failed`/`loaded` flag left over from the PREVIOUS user's photo —
  // which is what could make the initials fallback fail to appear (or
  // appear incorrectly) for the next user in the list.
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  const initials = getInitials(name);

  // FIX: mirrors Flutter's CachedNetworkImage exactly — its `placeholder`
  // (shown while loading) and `errorWidget` (shown on failure) are both
  // just this same initials view. The previous version only swapped to the
  // placeholder *after* an onError fired, so there was a window — every
  // single time, on every image — where nothing (or the browser's own
  // blank/broken-image box) showed instead of the initials. Now the
  // initials are the default any time there's no source, it hasn't
  // finished loading yet, or it failed; the real photo only ever replaces
  // them once it has actually finished loading successfully.
  const showPlaceholder = !src || !loaded || failed;

  return (
    <div
      onClick={onClick}
      style={{
        width: size,
        height: size,
        cursor: onClick ? 'pointer' : 'default',
        flexShrink: 0,
      }}
    >
      {showPlaceholder && (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: 'rgb(99, 53, 0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {initials ? (
            <span
              style={{
                color: '#FFFFFF',
                fontSize: size * 0.36,
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              {initials}
            </span>
          ) : (
            <svg
              width={size * 0.5}
              height={size * 0.5}
              viewBox="0 0 24 24"
              fill="#FFFFFF"
            >
              <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.5c-3.3 0-9.8 1.6-9.8 4.9v2.4h19.6v-2.4c0-3.3-6.5-4.9-9.8-4.9z" />
            </svg>
          )}
        </div>
      )}

      {/* Kept mounted (but hidden) while loading so onLoad/onError still
          fire — a display:none <img> still fetches and reports its result,
          it just doesn't paint until `loaded` flips true. */}
      {src && !failed && (
        <img
          src={src}
          alt={name || 'avatar'}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            display: loaded ? 'block' : 'none',
          }}
        />
      )}
    </div>
  );
}

export default UserAvatar;
export { UserAvatar };